import React, { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'

import { Dashboard, PageLayout } from '@components/common'
import {
  BaseContent,
  Button,
  EmptyState,
  HeadContent,
  Icon,
  Loader,
  Loading,
  ToolbarButton,
  useMediaQueryContext,
} from '@fourthwall/components'
import { DownloadIcon, UploadIcon } from '@fourthwall/icons'
import { routing } from '@utils/routing'

import * as S from './Fulfillments.styled'
import { useFulfillments } from './hooks/useFulfillments'

export const Fulfillments = () => {
  const media = useMediaQueryContext()
  const { ordersToManuallyFulfill, isLoading, isDownloading, isUploading, downloadCSV, uploadCSV } =
    useFulfillments()

  const title = `To be shipped ${ordersToManuallyFulfill ? `(${ordersToManuallyFulfill})` : ''}`

  const onDrop = useCallback<(files: File[]) => void>(
    (acceptedFiles) => {
      if (acceptedFiles[0] && uploadCSV) {
        const payload = new FormData()

        payload.append('csv', acceptedFiles[0])

        uploadCSV(payload)
      }
    },
    [uploadCSV],
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    maxSize: 104857600,
    disabled: isUploading,
    multiple: false,
  })

  const renderToolbar = () => {
    if (!ordersToManuallyFulfill) return null

    if (!media.tablet) {
      return (
        <ToolbarButton
          label="Download CSV of unshipped orders"
          icon={DownloadIcon}
          isLoading={isDownloading}
          onClick={downloadCSV}
        />
      )
    }

    return (
      <Button
        label="Download CSV of unshipped orders"
        size="small"
        loading={isDownloading}
        onClick={downloadCSV}
      />
    )
  }

  const renderHeader = () => {
    if (!ordersToManuallyFulfill) return null

    return (
      <BaseContent appearance="primary">
        You currently have {ordersToManuallyFulfill} orders that need to be shipped. To access
        shipping information{' '}
        <button onClick={downloadCSV}>download the CSV of unshipped orders</button>. Once the orders
        are shipped you can add the tracking numbers to the downloaded CSV, then upload it below.{' '}
        <a href={routing.external.helpCenter.selfFulfillment} target="_blank" rel="noreferrer">
          Learn more
        </a>
      </BaseContent>
    )
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <S.LoaderContainer>
          <Loader size="large" />{' '}
        </S.LoaderContainer>
      )
    }

    if (!ordersToManuallyFulfill) {
      return (
        <S.EmptyStateContainer>
          <EmptyState title="You don’t have any orders waiting to be shipped" />
        </S.EmptyStateContainer>
      )
    }

    if (!uploadCSV) return null

    return (
      <Loading isLoading={isUploading}>
        <S.UploadContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <S.UploadContent>
            <div style={{ marginBottom: 20 }}>
              <Icon component={UploadIcon} height={22} />
            </div>
            {media.tablet && (
              <>
                <div style={{ marginBottom: 26 }}>Drag and drop CSV with tracking information</div>
                <S.Divider>or</S.Divider>
              </>
            )}
            <Button label="Upload CSV with tracking information" appearance="primary" />
          </S.UploadContent>
        </S.UploadContainer>
      </Loading>
    )
  }

  return (
    <>
      <HeadContent title={title} />
      <PageLayout size="large">
        <Dashboard.Section
          name={title}
          toolbarContent={renderToolbar()}
          title={renderHeader()}
          titleSpacing="large"
        >
          {renderContent()}
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
